import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { inventoryApi } from '../../api/inventory-api-instance';
import { ApiError } from '../../api/errors';
import type { CreateSubscriptionInput, Subscription } from '../../domain/inventory';

function toDateInputValue(value: string | undefined): string {
  return value ? value.slice(0, 10) : '';
}

function fromDateInputValue(value: string): string {
  return new Date(`${value}T00:00:00.000Z`).toISOString();
}

function addDays(value: string | undefined, days: number): string {
  const base = value ? new Date(value) : new Date();
  base.setUTCDate(base.getUTCDate() + days);
  return base.toISOString();
}

function addYears(value: string, years: number): string {
  const date = new Date(value);
  date.setUTCFullYear(date.getUTCFullYear() + years);
  return date.toISOString();
}

interface NewContractTermDialogProps {
  customerId: string;
  currentSubscription: Subscription;
  onClose: () => void;
}

/**
 * Distinct from EditSubscriptionDrawer: this calls CreateSubscription, which
 * inserts a new subscriptions row rather than patching the current one — the
 * backend's own model of a contract renewal. See
 * docs/decisions/ADR-0007-customer-entitlements-workspace.md.
 */
export function NewContractTermDialog({
  customerId,
  currentSubscription,
  onClose,
}: NewContractTermDialogProps) {
  const queryClient = useQueryClient();
  const [startDate, setStartDate] = useState(() =>
    toDateInputValue(addDays(currentSubscription.endTime, 1)),
  );
  const [endDate, setEndDate] = useState(() =>
    toDateInputValue(addYears(addDays(currentSubscription.endTime, 1), 1)),
  );
  const [formError, setFormError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (input: CreateSubscriptionInput) =>
      inventoryApi.createCustomerSubscription(customerId, input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['customer-subscription', customerId] });
      onClose();
    },
    onError: (error: unknown) => {
      setFormError(
        error instanceof ApiError ? error.message : 'The new contract term could not be created.',
      );
    },
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setFormError(null);
    mutation.mutate({
      startTime: fromDateInputValue(startDate),
      endTime: fromDateInputValue(endDate),
      contractName: currentSubscription.contractName,
      contractUrl: currentSubscription.contractUrl,
      notes: currentSubscription.notes,
      numberOfSystems: currentSubscription.numberOfSystems,
      regions: currentSubscription.regions,
    });
  }

  return (
    <div className="drawer-overlay centered-overlay" role="presentation" onClick={onClose}>
      <form
        className="confirmation-dialog"
        aria-label="Start a new contract term"
        onClick={(event) => event.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <div className="drawer-heading">
          <div>
            <h2>Start a new contract term</h2>
          </div>
          <button
            type="button"
            className="icon-button"
            onClick={onClose}
            aria-label="Close new contract term dialog"
          >
            ×
          </button>
        </div>
        <p>Creates a separate entitlement term.</p>
        <div className="warning-callout">
          Current term revision {currentSubscription.revision} remains in history. Review dates and
          features before creating.
        </div>
        {formError ? (
          <p role="alert" className="form-error">
            {formError}
          </p>
        ) : null}
        <div className="form-row">
          <label>
            New term start date
            <input
              type="date"
              required
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
            />
          </label>
          <label>
            New term end date
            <input
              type="date"
              required
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
            />
          </label>
        </div>
        <div className="dialog-actions">
          <button type="button" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </button>
          <button type="submit" className="primary" disabled={mutation.isPending}>
            {mutation.isPending ? 'Creating…' : 'Create new term'}
          </button>
        </div>
      </form>
    </div>
  );
}
